import React from "react";
import { Box, Divider, Typography } from "@material-ui/core";
import { ListingSearchResult } from "redmond";
import { PolicyEnum } from "redmond/apis/tysons/vacation-rentals";
import dayjs from "dayjs";
import { useDeviceTypes } from "halifax";
import * as constants from "./constants";
import "./styles.scss";

export interface VRHouseRulesProps {
  selectedListing: ListingSearchResult;
  isMobile?: boolean;
}

export const VRHouseRules = ({
  selectedListing,
  isMobile = false,
}: VRHouseRulesProps) => {
  const { matchesMobile } = useDeviceTypes();

  const { listing } = selectedListing;
  const { terms } = listing;
  const {
    checkinInformation,
    checkInTimes,
    checkOutTimes,
    petFriendly = false,
    petsPolicy,
  } = terms;

  const cancellationPolicy =
    selectedListing.availability.rate?.cancellationPolicy;
  const paymentPolicy = selectedListing.availability.rate?.paymentPolicy;

  const cxlPolicyPrimaryText = cancellationPolicy?.policy?.primaryText;
  const cxlPolicyFullyRefundable =
    cancellationPolicy?.policy?.Policy === PolicyEnum.FullyRefundable;
  let cxlPolicySecondaryText: string | undefined;

  if (cancellationPolicy) {
    cxlPolicySecondaryText =
      cancellationPolicy.policy?.Policy !== PolicyEnum.Unknown
        ? cancellationPolicy.policy?.secondaryText
        : undefined;
  }

  const formatTimeRange = (startTime?: string, endTime?: string) => {
    let formatted = "";
    if (startTime && endTime) {
      formatted = `From ${startTime} - ${endTime}`;
    } else if (startTime) {
      formatted = `From ${startTime}`;
    } else if (endTime) {
      formatted = `Until ${endTime}`;
    }
    return formatted;
  };

  const initialText = paymentPolicy?.schedule
    ? paymentPolicy?.schedule.PaymentSchedule === "FullPaymentDueNow"
      ? constants.PAYMENT_POLICY_FULL_PAYMENT_DUE_NOW
      : paymentPolicy?.schedule.PaymentSchedule === "PaymentText"
      ? paymentPolicy.schedule.text
      : constants.PAYMENT_POLICY_FULL_PAYMENT_DUE_NOW
    : "";

  const clausesText = (paymentPolicy?.clauses || [])
    .map((clause) => {
      switch (clause.PaymentClause) {
        case "Text":
          return clause.description || "";
        case "CardOnFile":
          return clause.description || constants.PAYMENT_POLICY_CARD_ON_FILE;
        default:
          return "";
      }
    })
    .filter((text) => text.length > 0);

  const paymentPolicyText = [initialText, ...clausesText]
    .filter(Boolean)
    .join("\n");

  const getStandardTime = (time?: string) => {
    return time ? dayjs(time, "HH:mm").format("h:mm A") : undefined;
  };

  return (
    <Box
      className={`section vr-house-rules ${isMobile ? "mobile" : ""}`}
      style={{
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: "10px",
      }}
    >
      <Box paddingX={matchesMobile ? "20px" : "30px"}>
        <Typography className="section-title" variant="h2">
          {constants.HOUSE_RULES_TITLE}
        </Typography>
      </Box>
      {isMobile && <Divider className="divider-margin" />}
      <Box className="check-in-section">
        {!!checkinInformation && (
          <Box display="flex" flexDirection="column">
            <Typography className="subsection-title">
              {constants.CHECK_IN_INSTRUCTIONS_TITLE}
            </Typography>
            <Typography className="section-text">
              {constants.GENERAL_CHECK_IN_INSTRUCTIONS_TEXT}
            </Typography>
          </Box>
        )}

        {!!checkInTimes?.startTime && (
          <Box display="flex" flexDirection="column">
            <Typography className="subsection-title">
              {constants.CHECK_IN_TIME_TITLE}
            </Typography>
            <Typography className="section-text">
              {formatTimeRange(
                getStandardTime(checkInTimes.startTime),
                !!checkInTimes?.endTime
                  ? getStandardTime(checkInTimes.endTime)
                  : undefined
              )}
            </Typography>
          </Box>
        )}
        {!!(checkOutTimes?.startTime || checkOutTimes?.endTime) && (
          <Box display="flex" flexDirection="column">
            <Typography className="subsection-title">
              {constants.CHECKOUT_TIME_TITLE}
            </Typography>
            <Typography className="section-text-checkout">
              {formatTimeRange(
                getStandardTime(checkOutTimes?.startTime),
                !!checkOutTimes?.endTime
                  ? getStandardTime(checkOutTimes?.endTime)
                  : undefined
              )}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider />
      <Box className="policy-section">
        <Typography className="policy-title">
          {constants.PETS_POLICY_TITLE}
        </Typography>
        {petFriendly ? (
          petsPolicy ? (
            <Typography className="policy-text">
              {petsPolicy.split("\n").map((line, index, array) => (
                <React.Fragment key={index}>
                  {line}
                  {index < array.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
          ) : (
            <Typography className="policy-text">
              {constants.PETS_ALLOWED_TEXT}
            </Typography>
          )
        ) : (
          <Typography className="policy-text">
            {constants.PETS_NOT_ALLOWED_TEXT}
          </Typography>
        )}
      </Box>

      {cxlPolicyPrimaryText && (
        <>
          <Divider />
          <Box className="policy-section">
            <Typography
              className={
                cxlPolicyFullyRefundable
                  ? "cancellation-title-refundable"
                  : "cancellation-title"
              }
            >
              {cxlPolicyPrimaryText}
            </Typography>
            {cxlPolicySecondaryText && (
              <Typography className="policy-text">
                {cxlPolicySecondaryText}
              </Typography>
            )}
          </Box>
        </>
      )}

      {paymentPolicyText && (
        <>
          <Divider />
          <Box className="policy-section">
            <Typography className="policy-title">
              {constants.PAYMENT_POLICY_TITLE}
            </Typography>
            <Typography className="policy-text">{paymentPolicyText}</Typography>
          </Box>
        </>
      )}
      {listing.content.registrationId && (
        <>
          <Divider />
          <Box className="policy-section">
            <Typography className="registration-title">
              {constants.REGISTRATION_NUM_TITLE}
            </Typography>
            <Typography className="policy-text">
              {listing.content.registrationId}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};
